import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  activateAccount,
  clearSession,
  fetchHouses,
  setSessionHouse,
  type SessionHouse,
} from '../../../platform/auth/session';
import { Icon } from '../../../components/ui/Icon';
import { AuthAside } from '../AuthAside';

interface InvitationLink {
  token: string;
  email: string;
}

/**
 * O segredo chega no fragmento (`#token=`), que não trafega para servidor
 * nenhum, no mesmo formato do link do Phoenix A. Lido uma vez e removido da
 * barra de endereço para não ficar no histórico.
 */
function readInvitationLink(): InvitationLink {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const link = {
    token: fragment.get('token') ?? '',
    email: query.get('email') ?? fragment.get('email') ?? '',
  };
  if (window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  return link;
}

export function ActivatePage() {
  const navigate = useNavigate();
  const [link] = useState(readInvitationLink);
  const [email, setEmail] = useState(link.email);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [houses, setHouses] = useState<SessionHouse[]>([]);

  function enterHouse(house: SessionHouse) {
    setSessionHouse(house);
    void navigate('/', { replace: true });
  }

  async function handleSubmit(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (password !== confirmation) {
      setError('As senhas não conferem.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await activateAccount(email.trim().toLowerCase(), link.token, password);
      const available = await fetchHouses(session);
      if (available.length === 0) {
        clearSession();
        setError(
          'Seu acesso foi ativado, mas ainda não está liberado em nenhuma casa. Fale com a administração da casa.',
        );
        return;
      }
      const only = available.length === 1 ? available[0] : undefined;
      if (only) {
        enterHouse(only);
        return;
      }
      setHouses(available);
    } catch (err) {
      clearSession();
      setError(err instanceof Error ? err.message : 'Não foi possível ativar o acesso.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <AuthAside />
      <main className="auth-main">
        <div className="auth-card">
          <h2>Você foi convidado(a).</h2>
          <p>A administração da casa liberou seu acesso para a operação no Zeladoria.</p>

          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}

          {!link.token ? (
            <>
              <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                Este link de convite está incompleto. Abra novamente o link recebido por e-mail ou
                entre com seu e-mail para receber um novo convite.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  void navigate('/login');
                }}
                style={{ width: '100%', minHeight: 48 }}
              >
                Ir para a entrada
                <Icon name="arrow" size={16} />
              </button>
            </>
          ) : houses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {houses.map((house) => (
                <button
                  key={house.id}
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    enterHouse(house);
                  }}
                  style={{ justifyContent: 'space-between', minHeight: 52 }}
                >
                  {house.name}
                  <Icon name="arrow" size={16} />
                </button>
              ))}
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                void handleSubmit(event);
              }}
            >
              <div className="field">
                <label htmlFor="activate-email">Seu e-mail</label>
                <input
                  id="activate-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="activate-password">Crie sua senha</label>
                <input
                  id="activate-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="activate-confirmation">Repita a senha</label>
                <input
                  id="activate-confirmation"
                  type="password"
                  value={confirmation}
                  onChange={(e) => {
                    setConfirmation(e.target.value);
                  }}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
                style={{ width: '100%', minHeight: 48 }}
              >
                {busy ? 'Ativando…' : 'Ativar e entrar'}
                <Icon name="arrow" size={16} />
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
